/**
 * That a Google connection happened — never the connection itself.
 *
 * gauth.js keeps the access token in a closure and names no browser store at
 * all, and a test asserts it stays that way. Nothing here softens that. What
 * is written down is one bit meaning "this person has said yes to Google
 * before", which is worth nothing to whoever reads it: it is not a credential,
 * it opens nothing, and Google is still the only thing that can turn it into
 * access.
 *
 * That bit is enough. `prompt: ''` is answered silently once consent is on
 * record, so a page that knows a connection happened can have the calendar
 * back without a dialog and without ever having kept a token — which is the
 * whole point. The alternative, storing the token, would leave a bearer
 * credential for a year of somebody's life readable by any script that ever
 * ran on this origin.
 *
 * Every function here swallows what storage throws. iOS Safari in private
 * browsing has historically thrown on `setItem` rather than quietly doing
 * nothing, and storage is absent entirely when cookies are blocked. A calendar
 * that cannot remember is a nuisance; a page that will not boot is not.
 */

const KEY = 'alter.google.connected';

// One exact value counts. Anything can write to localStorage on this origin,
// so 'false', '0' and a stolen token must all read as "no" rather than as
// "some value is present, so yes".
const YES = '1';

export function rememberConnection(storage) {
  try {
    storage?.setItem(KEY, YES);
  } catch {
    // Remembering is a convenience. Failing to is not worth a word on screen.
  }
}

export function wasConnected(storage) {
  try {
    return storage?.getItem(KEY) === YES;
  } catch {
    return false;
  }
}

export function forgetConnection(storage) {
  try {
    storage?.removeItem(KEY);
  } catch {
    // Already unreachable; there is nothing left to do about it.
  }
}
